import { useEffect, useMemo, useState } from "react";
import { Compass } from "lucide-react";
import { Container } from "@/components/ui/container";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { ZeltFinder } from "@/components/ZeltFinder";
import { useSeo, generateBreadcrumbSchema } from "@/hooks/useSeo";
import {
  FINDER_BUDGET,
  FINDER_EINSATZ,
  FINDER_PRIORITAET,
  type FinderAnswers,
  type FinderBudget,
  type FinderEinsatz,
  type FinderPrioritaet,
  type FinderResult,
  type Zelt,
} from "@/data/zelte";

interface FinderSeiteProps {
  onNavigate: (path: string) => void;
  onToggleVergleich: (zelt: Zelt) => void;
  vergleichIds: string[];
}

function parseAnswersFromUrl(): Partial<FinderAnswers> {
  const params = new URLSearchParams(window.location.search);
  const personen = Number(params.get("personen"));
  const einsatz = params.get("einsatz") as FinderEinsatz | null;
  const budget = params.get("budget") as FinderBudget | null;
  const prioritaet = params.get("prioritaet") as FinderPrioritaet | null;

  const answers: Partial<FinderAnswers> = {};
  if (Number.isFinite(personen) && personen > 0) answers.personen = personen;
  if (einsatz && (FINDER_EINSATZ as readonly string[]).includes(einsatz)) answers.einsatz = einsatz;
  if (budget && (FINDER_BUDGET as readonly string[]).includes(budget)) answers.budget = budget;
  if (prioritaet && (FINDER_PRIORITAET as readonly string[]).includes(prioritaet)) answers.prioritaet = prioritaet;
  return answers;
}

function isComplete(answers: Partial<FinderAnswers>): answers is FinderAnswers {
  return (
    typeof answers.personen === "number" &&
    Boolean(answers.einsatz) &&
    Boolean(answers.budget) &&
    Boolean(answers.prioritaet)
  );
}

export function FinderSeite({ onNavigate, onToggleVergleich, vergleichIds }: FinderSeiteProps) {
  const initialAnswers = useMemo(() => parseAnswersFromUrl(), []);
  const [results, setResults] = useState<FinderResult[]>([]);
  const [loading, setLoading] = useState(isComplete(initialAnswers));

  useSeo({
    title: "Zelt-Finder - In 4 Fragen zum passenden Zelt | zelt.kaufen",
    description:
      "Finden Sie in vier Fragen das passende Zelt: Personenanzahl, Einsatz, Budget und Priorität. Persönliche Empfehlungen aus unserem Vergleich.",
    keywords: "Zelt Finder, Zeltwahl, welches Zelt, Zelt Empfehlung, Campingzelt finden",
    type: "website",
    schema: generateBreadcrumbSchema([
      { name: "Startseite", url: window.location.origin },
      { name: "Zelt-Finder", url: `${window.location.origin}/finder` },
    ]),
  });

  const fetchResults = (answers: FinderAnswers) => {
    setLoading(true);
    const params = new URLSearchParams({
      personen: String(answers.personen),
      einsatz: answers.einsatz,
      budget: answers.budget,
      prioritaet: answers.prioritaet,
    });
    const nextUrl = `/finder?${params.toString()}`;
    if (window.location.pathname + window.location.search !== nextUrl) {
      window.history.replaceState({}, "", nextUrl);
    }

    fetch(`/api/finder?${params.toString()}`)
      .then(res => res.json())
      .then(data => {
        setResults(data.data || []);
        setLoading(false);
      })
      .catch(() => {
        setResults([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isComplete(initialAnswers)) fetchResults(initialAnswers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container className="py-8 pb-24">
      <Breadcrumb items={[{ label: "Zelt-Finder" }]} className="mb-6" />

      <div className="max-w-3xl mb-10">
        <div className="inline-flex items-center gap-2 text-sm font-medium text-primary mb-3">
          <Compass className="h-4 w-4" />
          Persönliche Kaufberatung
        </div>
        <h1 className="text-3xl md:text-5xl font-bold mb-4">Zelt-Finder</h1>
        <p className="text-lg text-muted-foreground leading-relaxed">
          Vier kurze Fragen, dann sehen Sie die Modelle, die zu Ihrer Gruppe, Ihrem Einsatz und Ihrem Budget passen.
        </p>
      </div>

      <ZeltFinder
        onNavigate={onNavigate}
        onToggleVergleich={onToggleVergleich}
        vergleichIds={vergleichIds}
        initialAnswers={initialAnswers}
        results={results}
        loading={loading}
        onSubmit={fetchResults}
        onReset={() => {
          setResults([]);
          window.history.replaceState({}, "", "/finder");
        }}
      />
    </Container>
  );
}
